import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Gavel, CheckCircle2, Clock, Package, Heart, TrendingDown, PiggyBank, BarChart3, Sparkles, Gift, Copy, Lock, FileText, RotateCcw, Tag, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import Countdown from "../components/Countdown";
import PaymentScoreBadge from "../components/PaymentScoreBadge";
import LoyaltyInfoDialog from "../components/LoyaltyInfoDialog";
import RecurringOrdersPanel from "../components/RecurringOrdersPanel";
import PendingRatingsBanner from "../components/PendingRatingsBanner";
import BillingDataPanel from "../components/BillingDataPanel";
import WhatsAppPrefs from "../components/WhatsAppPrefs";
import ChangePasswordCard from "../components/ChangePasswordCard";
import RefundsHistory from "../components/RefundsHistory";
import CreditHistory from "../components/CreditHistory";
import { useAuth } from "../context/AuthContext";
import { usePush } from "../hooks/usePush";

const STATUS_LABEL = {
  open: "In asta", awarded: "Aggiudicato", paid: "Pagato",
  delivered: "Consegnato", expired: "Scaduto", cancelled: "Annullato", reassigning: "In riassegnazione",
};
const STATUS_COLOR = {
  open: "bg-amber-100 text-amber-700 border-amber-200",
  awarded: "bg-blue-100 text-blue-700 border-blue-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  delivered: "bg-slate-100 text-slate-700 border-slate-200",
  expired: "bg-red-100 text-red-700 border-red-200",
  reassigning: "bg-orange-100 text-orange-700 border-orange-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function RestaurantDashboard() {
  const { user, refresh } = useAuth();
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const push = usePush();

  const isPremium = user?.plan === "premium";

  const togglePush = async () => {
    const r = push.subscribed ? await push.unsubscribe() : await push.subscribe();
    if (r.ok) toast.success(push.subscribed ? "Notifiche push disattivate" : "Notifiche push attivate!");
    else toast.error(r.error);
  };

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/premium/checkout", { origin_url: window.location.origin });
      const target = (window.top && window.top !== window) ? window.top : window;
      try { target.location.href = data.checkout_url; }
      catch (_e) { window.location.href = data.checkout_url; }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setUpgrading(false); }
  };

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/premium/portal", { origin_url: window.location.origin });
      const target = (window.top && window.top !== window) ? window.top : window;
      try { target.location.href = data.portal_url; }
      catch (_e) { window.location.href = data.portal_url; }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setPortalBusy(false); }
  };

  const copyRef = () => {
    if (!user?.referral_code) return;
    navigator.clipboard.writeText(user.referral_code);
    toast.success("Codice copiato!");
  };

  useEffect(() => {
    let m = true;
    const load = async () => {
      try {
        const [o, f, a] = await Promise.all([
          api.get("/orders"),
          api.get("/restaurant/favorites").catch(() => ({ data: [] })),
          api.get("/analytics/restaurant").catch(() => ({ data: null })),
        ]);
        if (m) { setOrders(o.data); setFavorites(f.data); setAnalytics(a.data); }
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("restaurant dashboard load failed:", e); } finally { if (m) setLoading(false); }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { m = false; clearInterval(t); };
  }, []);

  const active = orders.filter(o => o.status === "open");
  const awarded = orders.filter(o => ["awarded", "paid", "reassigning"].includes(o.status));
  const done = orders.filter(o => ["delivered", "cancelled", "expired"].includes(o.status));

  const removeFav = async (sid) => {
    try {
      await api.delete(`/restaurant/favorites/${sid}`);
      setFavorites(favorites.filter(f => f.id !== sid));
      toast.success("Rimosso dai preferiti");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const stats = [
    { l: "Aste attive", v: active.length, icon: Gavel, tab: "active" },
    { l: "Aggiudicati", v: awarded.length, icon: CheckCircle2, tab: "awarded" },
    { l: "Consegnati", v: orders.filter(o => o.status === "delivered").length, icon: Package, tab: "delivered" },
    { l: "Totali", v: orders.length, icon: Clock, tab: "all" },
  ];
  const deliveredOrders = orders.filter(o => o.status === "delivered");
  const [tab, setTab] = useState("active");
  const tabsRef = useRef(null);
  const selectTab = (t) => { setTab(t); setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Dashboard Acquirente</div>
          <h1 className="mt-1 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Ciao, {user?.business_name || user?.name}</h1>
          <p className="mt-1 text-sm text-slate-600">Gestisci ordini, aste, fornitori preferiti e risparmi.</p>
          <div className="mt-2"><LoyaltyInfoDialog role="restaurant" /></div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {user?.payment_score && (
            <div className="hidden sm:block" data-testid="rest-payment-score-inline">
              <PaymentScoreBadge score={user.payment_score} size="md" withDetails testId="rest-my-payment-score" />
            </div>
          )}
          {push.supported && push.enabled && (
            <Button variant="outline" onClick={togglePush} disabled={push.busy} className="rounded-full" data-testid="push-toggle-button">
              {push.subscribed ? <BellOff className="w-4 h-4 mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
              {push.subscribed ? "Push attive" : "Attiva push"}
            </Button>
          )}
          <Link to="/offerte">
            <Button variant="outline" className="rounded-full" data-testid="offers-link-button"><Tag className="w-4 h-4 mr-2" />Offerte fornitori</Button>
          </Link>
          <Link to="/nuovo-ordine">
            <Button className="ga-btn-primary rounded-full" data-testid="create-order-button"><Plus className="w-4 h-4 mr-2" />Nuovo ordine</Button>
          </Link>
        </div>
      </div>

      {user?.payment_score && (
        <div className="mt-4 sm:hidden">
          <PaymentScoreBadge score={user.payment_score} size="md" withDetails testId="rest-my-payment-score-mobile" />
        </div>
      )}

      <PendingRatingsBanner role="restaurant" testId="rest-pending-ratings-banner" />

      {user && <BillingDataPanel key={`${user.vat_number}|${user.sdi_code}|${user.pec_email}`} user={user} onSaved={refresh} />}
      <div className="mt-4"><WhatsAppPrefs /></div>
      <div className="mt-4"><ChangePasswordCard /></div>

      {/* Raggio di ricerca fornitori */}
      <div className="mt-4 ga-card p-4" data-testid="search-radius-card">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Raggio di ricerca fornitori</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Ricevi offerte solo da fornitori entro N km dal tuo indirizzo. 0 = illimitato.
            </div>
          </div>
          <input
            type="number"
            min={0}
            step="1"
            defaultValue={user?.search_radius_km || 0}
            onBlur={async (e) => {
              const val = Math.max(0, Number(e.target.value) || 0);
              try {
                await api.patch("/auth/me", { search_radius_km: val });
                await refresh();
                toast.success(val > 0 ? `Raggio impostato a ${val} km` : "Raggio illimitato");
              } catch (err) { toast.error(err.response?.data?.detail || err.message); }
            }}
            className="w-24 rounded-md border border-slate-200 px-3 py-1.5 font-mono text-sm text-right"
            data-testid="search-radius-input"
          />
          <span className="font-mono text-sm text-slate-500">km</span>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className={`ga-card p-5 ${isPremium ? "border-emerald-300 bg-emerald-50/40" : ""}`} data-testid="premium-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Piano {isPremium ? "Premium" : "Free"}
              </div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">
                {isPremium ? "Analytics avanzate sbloccate" : "Passa a Premium · € 9,99/mese"}
              </div>
              <div className="text-xs text-slate-600 mt-1">
                {isPremium ? "Grazie del supporto! Ordini illimitati + risparmio dettagliato." : "Sblocca analytics avanzate, ordini illimitati e supporto prioritario."}
              </div>
              {isPremium && (
                <div className="mt-3">
                  <a
                    href={`${API}/reports/restaurant/monthly?year=${new Date().getFullYear()}&month=${new Date().getMonth() + 1}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-700 hover:text-emerald-900 underline decoration-dotted underline-offset-4"
                    data-testid="download-monthly-report"
                  >
                    <FileText className="w-3.5 h-3.5" /> Scarica report mensile PDF
                  </a>
                </div>
              )}
            </div>
            {!isPremium && (
              <Button onClick={upgrade} disabled={upgrading} className="ga-btn-primary rounded-full shrink-0" data-testid="upgrade-premium-button">
                <Sparkles className="w-4 h-4 mr-2" />{upgrading ? "..." : "Attiva Premium"}
              </Button>
            )}
            {isPremium && (
              <Button onClick={openPortal} disabled={portalBusy} variant="outline" className="rounded-full shrink-0" data-testid="manage-subscription-button">
                {portalBusy ? "..." : "Gestisci abbonamento"}
              </Button>
            )}
          </div>
        </div>
        <div className="ga-card p-5" data-testid="referral-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" /> Programma invito · {user?.referral_count || 0} inviti validi · € {(user?.referral_credit || 0).toFixed(2)}
              </div>
              <div className="mt-1 font-display text-lg font-bold text-slate-900">Invita e sblocca Premium gratis</div>
              <div className="text-[11px] text-slate-500 mt-0.5">Un invito conta (e il bonus arriva) quando l'invitato completa il suo primo ordine pagato.</div>
              <div className="text-xs text-slate-600 mt-1">
                {user?.referral_tier?.next_at ? (
                  <>Ancora <b>{user.referral_tier.next_at - (user.referral_count || 0)}</b> inviti per ricevere <b>{user.referral_tier.next_reward_months} mese/i</b> Premium gratis.</>
                ) : (
                  <>Hai raggiunto il massimo: 3 mesi Premium gratis sbloccati! 🎉</>
                )}
              </div>
              {user?.referral_tier?.next_at && (
                <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{width: `${Math.min(100, ((user.referral_count || 0) / user.referral_tier.next_at) * 100)}%`}} />
                </div>
              )}
            </div>
            <button onClick={copyRef} className="shrink-0 flex items-center gap-2 font-mono text-sm font-bold bg-slate-900 text-white rounded-full px-4 py-2 hover:bg-slate-700" data-testid="copy-referral-code">
              {user?.referral_code || "—"} <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((s) => (
          <button type="button" key={s.l} onClick={() => selectTab(s.tab)}
            className={`ga-card p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-slate-400 cursor-pointer ${tab === s.tab ? "ring-2 ring-emerald-500 border-emerald-500" : ""}`}
            data-testid={`stat-${s.l.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{s.l}</div>
              <s.icon className="w-4 h-4 text-slate-400" />
            </div>
            <div className="mt-2 font-display text-3xl font-extrabold text-slate-900">{s.v}</div>
            <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700">Vedi dettagli →</div>
          </button>
        ))}
      </div>

      <div ref={tabsRef} />
      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="bg-white border border-slate-200 flex-wrap">
          <TabsTrigger value="active" data-testid="tab-active">In asta ({active.length})</TabsTrigger>
          <TabsTrigger value="awarded" data-testid="tab-awarded">Aggiudicati ({awarded.length})</TabsTrigger>
          <TabsTrigger value="delivered" data-testid="tab-delivered">Consegnati ({deliveredOrders.length})</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-done">Storico ({done.length})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Tutti ({orders.length})</TabsTrigger>
          <TabsTrigger value="refunds" data-testid="tab-refunds">Rimborsi</TabsTrigger>
          <TabsTrigger value="credit" data-testid="tab-credit">Credito € {(user?.referral_credit || 0).toFixed(2)}</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">Preferiti ({favorites.length})</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="tab-recurring">Ricorrenti</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="active" className="mt-6">
          <OrderList orders={active} empty="Nessuna asta attiva. Crea il tuo primo ordine." loading={loading} />
        </TabsContent>
        <TabsContent value="awarded" className="mt-6">
          <OrderList orders={awarded} empty="Nessun ordine aggiudicato al momento." loading={loading} />
        </TabsContent>
        <TabsContent value="delivered" className="mt-6">
          <OrderList orders={deliveredOrders} empty="Nessun ordine consegnato finora." loading={loading} />
        </TabsContent>
        <TabsContent value="all" className="mt-6">
          <OrderList orders={orders} empty="Nessun ordine. Crea il tuo primo ordine." loading={loading} />
        </TabsContent>
        <TabsContent value="refunds" className="mt-6">
          <RefundsHistory />
        </TabsContent>
        <TabsContent value="credit" className="mt-6">
          <CreditHistory />
        </TabsContent>
        <TabsContent value="done" className="mt-6">
          <OrderList orders={done} empty="Nessuno storico ancora." loading={loading} />
        </TabsContent>
        <TabsContent value="favorites" className="mt-6">
          <FavoritesList favorites={favorites} removeFav={removeFav} />
        </TabsContent>
        <TabsContent value="recurring" className="mt-6">
          <RecurringOrdersPanel />
        </TabsContent>
        <TabsContent value="analytics" className="mt-6">
          <AnalyticsPanel analytics={analytics} loading={loading} isPremium={isPremium} onUpgrade={upgrade} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrderList({ orders, empty, loading }) {
  if (loading) return <div className="text-sm text-slate-500 py-6">Caricamento...</div>;
  if (!orders.length) return <div className="ga-card p-8 text-center text-slate-500 text-sm">{empty}</div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {orders.map(o => (
        <Link key={o.id} to={`/ordine/${o.id}`} className="ga-card p-5 block" data-testid={`order-card-${o.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display text-lg font-bold text-slate-900 truncate">{o.title}</div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{o.category}</div>
            </div>
            <Badge className={`shrink-0 ${STATUS_COLOR[o.status] || ""}`}>{STATUS_LABEL[o.status] || o.status}</Badge>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-600">{o.bids_count} offert{o.bids_count === 1 ? "a" : "e"}</div>
            {o.lowest_price != null ? (
              <div className="font-mono text-sm font-bold text-emerald-700">€ {o.lowest_price.toFixed(2)}</div>
            ) : (
              <div className="text-xs text-slate-400">In attesa offerte</div>
            )}
          </div>
          {o.status === "delivered" && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.assign(`/nuovo-ordine?from=${o.id}`); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-700" data-testid={`reorder-button-${o.id}`}>
                <RotateCcw className="w-3.5 h-3.5" /> Riordina
              </button>
            </div>
          )}
          {o.status === "open" && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
              <div className="text-xs text-slate-500">{o.choice_ends_at ? "Scegli un'offerta entro" : "Chiude tra"}</div>
              <Countdown endsAt={o.choice_ends_at || o.auction_ends_at} testId={`countdown-${o.id}`} />
            </div>
          )}
          {o.winner && (
            <div className="mt-3 pt-3 border-t border-slate-100 text-sm text-slate-700">
              Vincitore: <span className="font-semibold">{o.winner.name}</span>
            </div>
          )}
        </Link>
      ))}
    </div>
  );
}

function FavoritesList({ favorites, removeFav }) {
  if (!favorites.length) {
    return (
      <div className="ga-card p-8 text-center text-sm text-slate-500">
        Non hai ancora aggiunto fornitori preferiti. Vai su un <span className="font-semibold text-slate-800">profilo fornitore</span> e premi il cuore.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {favorites.map(f => (
        <div key={f.id} className="ga-card p-5" data-testid={`fav-${f.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <Link to={`/fornitore/profilo/${f.id}`} className="font-display text-lg font-bold text-slate-900 truncate hover:underline">{f.name}</Link>
              <div className="text-xs text-slate-500 mt-1">★ {f.rating_avg} ({f.rating_count})</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {f.categories.slice(0, 3).map(c => <span key={c.id} className="text-xs bg-slate-100 rounded-full px-2 py-0.5 text-slate-700">{c.label}</span>)}
              </div>
            </div>
            <button onClick={() => removeFav(f.id)} className="text-red-500 hover:text-red-700" data-testid={`remove-fav-${f.id}`}>
              <Heart className="w-5 h-5 fill-red-500" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnalyticsPanel({ analytics, loading, isPremium, onUpgrade }) {
  if (loading) return <div className="text-sm text-slate-500 py-6">Caricamento...</div>;
  if (!analytics || analytics.total_orders === 0) {
    return <div className="ga-card p-8 text-center text-sm text-slate-500">Ancora nessun ordine completato. Le analytics compaiono dopo la prima consegna.</div>;
  }
  const cards = [
    { l: "Ordini completati", v: analytics.total_orders, icon: CheckCircle2, tone: "text-slate-900", premium: false },
    { l: "Spesa totale", v: `€ ${analytics.total_spent.toFixed(2)}`, icon: Package, tone: "text-slate-900", premium: false },
    { l: "Risparmio stimato", v: `€ ${analytics.total_saving.toFixed(2)}`, icon: PiggyBank, tone: "text-emerald-700", premium: true },
    { l: "Risparmio %", v: `${analytics.saving_pct}%`, icon: TrendingDown, tone: "text-emerald-700", premium: true },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={c.l} className={`ga-card p-5 relative ${c.premium && !isPremium ? "overflow-hidden" : ""}`} data-testid={`analytics-stat-${i}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{c.l}</div>
              <c.icon className={`w-4 h-4 text-slate-400`} />
            </div>
            <div className={`mt-2 font-display text-2xl font-extrabold ${c.tone} ${c.premium && !isPremium ? "blur-sm select-none" : ""}`}>{c.v}</div>
            {c.premium && !isPremium && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                <button onClick={onUpgrade} className="text-xs font-semibold text-slate-900 bg-amber-100 border border-amber-300 rounded-full px-3 py-1 flex items-center gap-1" data-testid={`unlock-${i}`}>
                  <Lock className="w-3 h-3" /> Sblocca con Premium
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {isPremium ? (
        <>
          <div className="ga-card p-6">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-slate-700" />
              <h3 className="font-display text-xl font-bold text-slate-900">Per categoria</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {analytics.per_category.map(c => (
                <div key={c.id} className="py-3 grid grid-cols-6 gap-3 items-center text-sm">
                  <div className="col-span-2 font-medium text-slate-900 truncate">{c.label}</div>
                  <div className="text-xs text-slate-500">{c.orders} ordine{c.orders === 1 ? "" : "i"}</div>
                  <div className="font-mono text-slate-700">€ {c.spent.toFixed(2)}</div>
                  <div className="text-xs text-slate-500 font-mono">media € {c.avg_market.toFixed(2)}</div>
                  <div className="font-mono text-emerald-700 font-semibold">-€ {c.saving.toFixed(2)}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="ga-card p-6">
            <h3 className="font-display text-xl font-bold text-slate-900 mb-3">Ultimi ordini</h3>
            <div className="divide-y divide-slate-100">
              {analytics.recent_orders.map(o => (
                <Link to={`/ordine/${o.id}`} key={o.id} className="py-3 grid grid-cols-6 gap-3 items-center text-sm hover:bg-slate-50 -mx-2 px-2 rounded-lg" data-testid={`analytics-order-${o.id}`}>
                  <div className="col-span-2 font-medium text-slate-900 truncate">{o.title}</div>
                  <div className="text-xs text-slate-500">{o.category_label}</div>
                  <div className="font-mono text-slate-700">€ {o.price.toFixed(2)}</div>
                  <div className="text-xs text-slate-500 font-mono">vs € {o.category_avg.toFixed(2)}</div>
                  <div className="font-mono text-emerald-700 font-semibold">
                    {o.saving > 0 ? `-€ ${o.saving.toFixed(2)} (${o.saving_pct.toFixed(0)}%)` : "—"}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="ga-card p-8 text-center border-amber-200 bg-amber-50/40">
          <Lock className="w-8 h-8 text-amber-600 mx-auto" />
          <h3 className="mt-3 font-display text-xl font-bold text-slate-900">Analytics avanzate riservate a Premium</h3>
          <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">Confronto storico per categoria e dettaglio ordine per ordine. Sblocca con l'abbonamento mensile.</p>
          <Button onClick={onUpgrade} className="mt-4 ga-btn-primary rounded-full" data-testid="analytics-upgrade-button">
            <Sparkles className="w-4 h-4 mr-2" />Attiva Premium € 9,99/mese
          </Button>
        </div>
      )}
    </div>
  );
}
